import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFetchAllSchemeMutation } from "../../../features/tenant/scheme/schemeApi";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import { getVendorName } from "../../../utils/getVendorName";
import { getToken, getTenantId } from "../../../components/Scheme/helpers";
import SchemePanel from "../../../components/Scheme/SchemePanel";
import StatsModal from "../../../components/Scheme/StatsModal";
import WinnerBanner from "../../../components/Scheme/WinnerBanner";
import { computeWinner } from "../../../components/Scheme/computeWinner";

const MAX_SCHEMES = 2;

const SchemeStats = () => {
  const navigate = useNavigate();
  const [fetchAllScheme, { data: schemeData, isLoading: schemeIsLoading }] =
    useFetchAllSchemeMutation();

  const [schemes, setSchemes] = useState([]);
  // panels[i] = selected scheme object or null
  const [panels, setPanels] = useState([null]);
  const [selectedIds, setSelectedIds] = useState([""]);
  // statsMap[i] = stats data for panel i
  const [statsMap, setStatsMap] = useState({});
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchAllScheme({ token: getToken(), tenant_id: getTenantId(), body: {} });
  }, []);

  useEffect(() => {
    if (schemeData?.body?.data) setSchemes(schemeData.body.data);
  }, [schemeData]);

  const handleSelectScheme = (index, id) => {
    const scheme = schemes.find((s) => s.id === Number(id)) ?? null;
    const newPanels = [...panels];
    newPanels[index] = scheme;
    const newIds = [...selectedIds];
    newIds[index] = id;
    setPanels(newPanels);
    setSelectedIds(newIds);
    // clear stats for this slot
    setStatsMap((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleAddPanel = () => {
    if (panels.length >= MAX_SCHEMES) return;
    setPanels([...panels, null]);
    setSelectedIds([...selectedIds, ""]);
  };

  const handleRemovePanel = (index) => {
    setPanels(panels.filter((_, i) => i !== index));
    setSelectedIds(selectedIds.filter((_, i) => i !== index));
    setStatsMap((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        const ki = Number(k);
        if (ki < index) next[ki] = prev[ki];
        else if (ki > index) next[ki - 1] = prev[ki];
      });
      return next;
    });
  };

  const handleStatsLoaded = (index, stats) => {
    setStatsMap((prev) =>
      stats ? { ...prev, [index]: stats } : { ...prev, [index]: null },
    );
  };

  const activePanels = panels
    .map((scheme, i) => ({ scheme, index: i }))
    .filter(({ scheme }) => scheme !== null);
  const chosenIds = selectedIds.map(Number).filter(Boolean);
  const isTwoSchemes = activePanels.length === 2;

  // stats for modal and winner
  const statsA = statsMap[0] ?? null;
  const statsB = statsMap[1] ?? null;
  const canViewStats = statsA !== null;
  const bothLoaded = statsA && statsB;
  const winnerResult =
    isTwoSchemes && bothLoaded
      ? computeWinner(panels[0]?.name, statsA, panels[1]?.name, statsB)
      : null;

  return (
    <div className="flex w-full bg-gray-100 min-h-screen justify-center">
      <div className="w-full">
        {/* Page Header */}
        <div className="border-b bg-white border-gray-300 pl-6 pr-4 py-2 shadow-sm flex items-center justify-between">
          <div className="text-xl font-bold text-gray-800">
            {isTwoSchemes ? "Compare Schemes" : "Scheme Stats"}
            <LinksComponent />
          </div>
          <button
            onClick={() => navigate(`/genefied/${getVendorName()}/scheme`)}
            className="text-sm text-white bg-gray-600 hover:bg-gray-700 font-medium rounded-lg px-4 py-2 transition-colors"
          >
            Back
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Scheme selector card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-bold text-gray-700">
                  {isTwoSchemes ? "Comparing 2 Schemes" : "Select a Scheme"}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {isTwoSchemes
                    ? "Set filters on each side and click View Stats, then see the winner below"
                    : "Add filters and click View Stats to see the breakdown"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* View Stats / Chart button */}
                {canViewStats && (
                  <button
                    onClick={() => setModalOpen(true)}
                    className="flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 px-4 py-2 rounded-lg shadow-sm transition-all"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                    View Stats
                  </button>
                )}
                {/* Add scheme button */}
                {panels.length < MAX_SCHEMES && (
                  <button
                    onClick={handleAddPanel}
                    className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 border border-blue-300 bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-lg transition-colors"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Compare with another
                  </button>
                )}
              </div>
            </div>

            {schemeIsLoading ? (
              <p className="text-sm text-gray-400">Loading schemes...</p>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                {panels.map((_, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && (
                      <div className="hidden sm:flex items-end pb-2 px-1">
                        <span className="text-sm font-black text-gray-300">
                          VS
                        </span>
                      </div>
                    )}
                    <div className="flex-1 flex items-end gap-2">
                      <div className="flex-1">
                        <label className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-1.5">
                          {isTwoSchemes
                            ? `Scheme ${index === 0 ? "A" : "B"}`
                            : "Scheme"}
                        </label>
                        <select
                          value={selectedIds[index]}
                          onChange={(e) =>
                            handleSelectScheme(index, e.target.value)
                          }
                          className={`w-full border border-gray-300 rounded-lg px-3 py-2.2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 shadow-sm ${index === 0 ? "focus:ring-blue-500" : "focus:ring-emerald-500"}`}
                        >
                          <option value="">Select scheme...</option>
                          {schemes
                            .filter(
                              (s) =>
                                !chosenIds.includes(s.id) ||
                                s.id === Number(selectedIds[index]),
                            )
                            .map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                        </select>
                      </div>
                      {index > 0 && (
                        <button
                          onClick={() => handleRemovePanel(index)}
                          className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 border border-gray-300 rounded-lg transition-colors"
                          title="Remove"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {/* Panels */}
          {activePanels.length > 0 ? (
            <>
              {/* VS divider for 2 schemes */}
              {isTwoSchemes && (
                <div className="relative flex items-center">
                  <div className="flex-1 border-t-2 border-blue-200" />
                  <div className="mx-4 flex items-center gap-3">
                    <span className="text-xs font-bold bg-blue-600 text-white px-3 py-1 rounded-full">
                      {panels[0]?.name}
                    </span>
                    <span className="text-xs font-black text-gray-400">VS</span>
                    <span className="text-xs font-bold bg-emerald-600 text-white px-3 py-1 rounded-full">
                      {panels[1]?.name}
                    </span>
                  </div>
                  <div className="flex-1 border-t-2 border-emerald-200" />
                </div>
              )}

              {/* Filter panels */}
              <div
                className={`grid grid-cols-1 ${isTwoSchemes ? "lg:grid-cols-2" : ""} gap-4`}
              >
                {activePanels.map(({ scheme, index }) => (
                  <SchemePanel
                    key={`${scheme.id}-${index}`}
                    scheme={scheme}
                    index={index}
                    totalPanels={activePanels.length}
                    onStatsLoaded={(stats) => handleStatsLoaded(index, stats)}
                    onRemove={() => handleRemovePanel(index)}
                    canRemove={panels.length > 1}
                  />
                ))}
              </div>

              {/* Waiting hint for second panel */}
              {isTwoSchemes && statsA && !statsB && (
                <div className="bg-white rounded-xl border border-dashed border-gray-300 px-6 py-5 text-center">
                  <p className="text-sm text-gray-500 font-medium">
                    Waiting for{" "}
                    <span className="text-emerald-600">{panels[1]?.name}</span>{" "}
                    stats...
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Click "View Stats" on the right panel to reveal the winner
                  </p>
                </div>
              )}

              {/* Winner banner */}
              {winnerResult && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
                    Winner
                  </p>
                  <WinnerBanner result={winnerResult} />
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <p className="text-sm font-semibold text-gray-400">
                Select a scheme to get started
              </p>
              <p className="text-xs text-gray-300 mt-1">
                You can also add a second scheme to compare side by side
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Stats Modal */}
      <StatsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        statsA={statsA}
        statsB={bothLoaded ? statsB : null}
        nameA={panels[0]?.name ?? "Scheme A"}
        nameB={panels[1]?.name ?? "Scheme B"}
        winner={winnerResult}
      />
    </div>
  );
};

export default SchemeStats;

